import { Component, effect, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { SectionComponent } from '../../../components/layout/section/section.component';
import {
  ITableSortParams,
  TableComponent,
  TableSortOrder,
  toSortOrder,
} from '../../../components/data-display/table/table.component';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { TableHeaderComponent } from '../../../components/data-display/table/table-header/table-header.component';
import { Subscription } from 'rxjs';
import { TextInputComponent } from '../../../components/data-input/form-inputs/text-input/text-input.component';
import { DATE_FORMAT_LONG, IIncident, IMessageText } from '@web/dtos';
import { MessageStore } from '../../../services/api/message/message-store.service';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { IncidentStore } from '../../../services/api/incident-monitor/incident-monitor-store.service';
import { IncidentChartComponent } from './incident-chart/incident-chart.component';
import { TableCellComponent } from '../../../components/data-display/table/table-cell/table-cell.component';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';

interface ISearchForm {
  search: FormControl<string | null>;
}

@Component({
  selector: 'aw-incident-monitor-page',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    LoadingAnimationComponent,
    SectionComponent,
    TableComponent,
    AppendTestIdPipe,
    TableHeaderComponent,
    TextInputComponent,
    LanguagePipe,
    IncidentChartComponent,
    TableCellComponent,
  ],
  templateUrl: './incident-monitor-page.component.html',
  styles: ``,
})
export class IncidentMonitorPageComponent implements OnDestroy {
  readonly messageStore: MessageStore = inject(MessageStore);
  readonly incidentStore: IncidentStore = inject(IncidentStore);
  readonly languagePipe: LanguagePipe = inject(LanguagePipe);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  protected readonly DATE_FORMAT_LONG: string = DATE_FORMAT_LONG;

  public readonly filterForm: FormGroup<ISearchForm> = new FormGroup({
    search: new FormControl<string | null>(null),
  });
  public readonly componentSubscriptions: Subscription = new Subscription();

  public allIncidents: IIncident[] = this.incidentStore.incidents();
  public filteredIncidents: IIncident[] = this.allIncidents;

  constructor() {
    effect((): void => {
      this.allIncidents = this.incidentStore.incidents();
      this.filteredIncidents = this.allIncidents;
    });

    this.componentSubscriptions.add(
      this.filterForm.valueChanges.subscribe(value => {
        this.filteredIncidents = this.filterIncidents(
          this.allIncidents,
          this.toTextFilterValue(value as any)
        );
      })
    );
  }

  ngOnDestroy(): void {
    this.componentSubscriptions.unsubscribe();
  }

  public filterIncidents(
    allSubscriptions: IIncident[],
    filter: any
  ): IIncident[] {
    return allSubscriptions.filter((s: IIncident): boolean =>
      this.searchIncludesSubstring(s, filter.search)
    );
  }

  private searchIncludesSubstring(
    subscription: IIncident,
    subStringFilter: string | null | undefined
  ): boolean {
    if (!subStringFilter) {
      return true;
    }

    const filter: string = subStringFilter.toLowerCase();

    return [
      subscription.code,
      this.languagePipe.transform(this.getMessage(subscription.code)),
    ].some((field: string): boolean => field?.toLowerCase().includes(filter));
  }

  private toTextFilterValue<T extends { search: string | null }>(
    value: T
  ): { search: null | string } {
    const compensateNull = (val: string | null) =>
      val === 'null' ? null : val;

    return {
      search: compensateNull(value.search),
    };
  }

  public incidentCodeFilter: ITableSortParams<IIncident>['compare'] = (
    a: IIncident,
    b: IIncident,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    return a.code.toLowerCase() > b.code.toLowerCase()
      ? 1 * sortDirection
      : -1 * sortDirection;
  };

  public incidentMessageFilter: ITableSortParams<IIncident>['compare'] = (
    a: IIncident,
    b: IIncident,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    const messageA = this.languagePipe.transform(this.getMessage(a.code));
    const messageB = this.languagePipe.transform(this.getMessage(b.code));
    return messageA.toLowerCase() > messageB.toLowerCase()
      ? 1 * sortDirection
      : -1 * sortDirection;
  };

  public dateCreatedAtFilter: ITableSortParams<IIncident>['compare'] = (
    a: IIncident,
    b: IIncident,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    const dateA = new Date(a.createdAt ?? new Date());
    const dateB = new Date(b.createdAt ?? new Date());
    return (dateA.getTime() - dateB.getTime()) * sortDirection;
  };

  getMessage(code: string): IMessageText {
    return this.messageStore
      .messages()
      .filter((msg: IMessageText) => msg.code === code)[0];
  }
}
